import { z } from "zod";
/**
 * Localised user-visible string. Skill bodies are English-only — these
 * fields cover labels rendered in the SPA (chat activity log, planner
 * system-prompt index) and surface to the couple in their app language.
 */
export const LocalizedSkillStringSchema = z.object({
    pl: z.string().min(1),
    en: z.string().min(1),
    de: z.string().min(1).optional(),
});
/** Resolve a localised skill string. `de` falls back to English when missing
 *  (German is optional on skill frontmatter); `en` returns English; anything
 *  else returns Polish — the original, always-present default. */
export function resolveLocalizedSkillString(s, locale) {
    if (locale === "de")
        return s.de ?? s.en;
    if (locale === "en")
        return s.en;
    return s.pl;
}
/**
 * When a skill is eligible to fire from the scheduled-review routine
 * (Cloud Function, shipped in Phase 2). Skills omit this field for
 * conversation-only behaviour.
 */
export const ScheduledSkillTriggerSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("days_after_signup"),
        value: z.number().int().positive(),
    }),
    z.object({
        type: z.literal("days_before_wedding"),
        value: z.number().int().positive(),
    }),
    z.object({
        type: z.literal("on_milestone"),
        milestone: z.enum(["first_guest_added", "first_table_created", "budget_50_percent"]),
    }),
]);
export const SkillAttachmentRequirementSchema = z.enum(["none", "image", "pdf", "any"]);
/**
 * Frontmatter parsed from a planner skill `.md` file. The body of the file
 * is the expert system-prompt fragment loaded into the chat session by the
 * `load_skill` tool.
 */
export const SkillFrontmatterSchema = z.object({
    id: z.string().regex(/^[a-z0-9-]+$/, "id must be kebab-case ASCII"),
    version: z.number().int().positive(),
    title: LocalizedSkillStringSchema,
    whenToUse: LocalizedSkillStringSchema,
    tags: z.array(z.string()).default([]),
    requiresAttachment: SkillAttachmentRequirementSchema.default("none"),
    suggestedTools: z.array(z.string()).default([]),
    schedule: ScheduledSkillTriggerSchema.optional(),
});
